import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Upload, Clock, XCircle, ImagePlus, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PayoutProofCard({ firmId }) {
  const { user } = useAuth();
  const [proof, setProof] = useState(undefined); // undefined = loading, null = none
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const shareCertificate = async () => {
    if (!proof?.id) return;
    setSharing(true);
    const url = `${api.defaults.baseURL}/payout-proofs/${proof.id}/certificate.png`;
    const caption = `Verified ${proof.amount ? `${proof.amount} ` : ""}payout${proof.firm_name ? ` from ${proof.firm_name}` : ""} — Funded Traders Club`;
    try {
      const blob = await fetch(url).then((r) => {
        if (!r.ok) throw new Error("certificate unavailable");
        return r.blob();
      });
      const fileObj = new File([blob], "ftc-payout-certificate.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [fileObj] })) {
        await navigator.share({ files: [fileObj], title: "Verified payout", text: caption });
        toast.success("CERTIFICATE SHARED");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = "ftc-payout-certificate.png";
        a.click();
        toast.success("CERTIFICATE DOWNLOADED — SHARE IT ANYWHERE");
      }
    } catch {
      toast.error("COULD NOT PREPARE CERTIFICATE");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    setProof(undefined);
    api.get("/payout-proofs/mine")
      .then(({ data }) => setProof(data.find((p) => p.firm_id === firmId) || null))
      .catch(() => setProof(null));
  }, [user, firmId]);

  if (!user) return null;

  const submit = async () => {
    if (!file) {
      toast.error("CHOOSE A SCREENSHOT FIRST");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("firm_id", firmId);
      fd.append("amount", amount);
      fd.append("file", file);
      const { data } = await api.post("/payout-proofs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProof(data);
      setFile(null);
      setAmount("");
      toast.success("PROOF SUBMITTED — UNDER REVIEW");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadBlock = (
    <div className="mt-5">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
        data-testid="proof-file-input"
        onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="proof-choose-btn"
          className="inline-flex items-center gap-2 rounded-xl border border-line bg-ink px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:border-signal/60">
          <ImagePlus size={14} className="text-signal" />
          {file ? file.name.slice(0, 28) : "Choose Screenshot"}
        </button>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="proof-amount-input"
          placeholder="Amount (optional, e.g. $2,450)"
          className="min-w-[180px] flex-1 rounded-xl border border-line bg-ink px-3.5 py-2.5 font-mono text-xs text-white outline-none transition-colors focus:border-signal" />
        <button type="button" onClick={submit} disabled={busy} data-testid="proof-submit-btn"
          className="btn-beam btn-gradient inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-white disabled:opacity-50">
          <Upload size={13} /> {busy ? "Uploading…" : "Submit Proof"}
        </button>
      </div>
      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.15em] text-muted">
        PNG/JPG/WEBP up to 5MB — the desk verifies it, then your reviews earn the badge
      </p>
    </div>
  );

  return (
    <div className="card-violet p-6 sm:p-7" data-testid="payout-proof-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Payout Certificate"}</p>
      {proof === undefined ? (
        <p className="mt-3 font-mono text-xs text-muted">CHECKING YOUR CERTIFICATE…</p>
      ) : proof?.status === "verified" ? (
        <div data-testid="proof-status-verified">
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-profit/30 bg-profit/[0.06] p-4 pulse-verified">
            <ShieldCheck size={22} className="text-profit" />
            <div>
              <p className="font-display text-lg font-800 text-white">Verified payout member</p>
              <p className="text-sm text-muted">Your reviews on this firm carry the Verified Payout badge.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={shareCertificate}
            disabled={sharing}
            data-testid="proof-share-btn"
            className="btn-beam btn-gradient-blue mt-4 inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] disabled:opacity-50"
          >
            <Share2 size={13} /> {sharing ? "Preparing…" : "Share Certificate"}
          </button>
        </div>
      ) : proof?.status === "pending" ? (
        <div className="mt-3 flex items-center gap-3" data-testid="proof-status-pending">
          <Clock size={22} className="text-gold" />
          <div>
            <p className="font-display text-lg font-800 text-white">Proof under review</p>
            <p className="text-sm text-muted">The desk is checking your screenshot — the badge appears once verified.</p>
          </div>
        </div>
      ) : (
        <>
          {proof?.status === "rejected" && (
            <div className="mt-3 mb-4 flex items-center gap-3" data-testid="proof-status-rejected">
              <XCircle size={22} className="text-loss" />
              <p className="text-sm text-muted">Last proof was rejected — upload a clearer payout screenshot.</p>
            </div>
          )}
          {!proof && (
            <h3 className="mt-2 font-display text-xl font-800 tracking-tight text-white">Get the Verified Payout badge</h3>
          )}
          <p className="mt-2 text-sm leading-relaxed text-muted">
            Upload a payout screenshot from this firm. Once the desk verifies it, every review you
            write here shows a <span className="text-signal">Verified Payout</span> badge.
          </p>
          {uploadBlock}
        </>
      )}
    </div>
  );
}
